import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import { api } from '@/api/endpoints'
import type { User, LoginRequest, RegisterRequest, LoginResponse } from '@/api/types'

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref<User | null>(null)
  const token = ref<string | null>(localStorage.getItem('auth_token'))
  const loading = ref(false)
  const error = ref<string | null>(null)
  const initialized = ref(false) // Флаг инициализации

  // Getters
  const isAuthenticated = computed(() => !!token.value && !!user.value)
  const isAdmin = computed(() => user.value?.is_superuser || false)

  // Actions
  const login = async (credentials: LoginRequest): Promise<LoginResponse> => {
    loading.value = true
    error.value = null

    try {
      const response = await api.auth.login(credentials)

      // Сохраняем токен и пользователя
      token.value = response.access_token
      user.value = response.user

      // Сохраняем токен в localStorage
      localStorage.setItem('auth_token', response.access_token)

      console.log('Login successful:', response.user)
      return response
    } catch (err: any) {
      error.value = err.detail || 'Ошибка входа в систему'
      console.error('Login error:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  const register = async (userData: RegisterRequest): Promise<User> => {
    loading.value = true
    error.value = null

    try {
      const newUser = await api.auth.register(userData)
      console.log('Registration successful:', newUser)
      return newUser
    } catch (err: any) {
      error.value = err.detail || 'Ошибка регистрации'
      console.error('Registration error:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  const logout = async (): Promise<void> => {
    loading.value = true

    try {
      // Вызываем API для logout (опционально)
      if (token.value) {
        await api.auth.logout()
      }
    } catch (err) {
      console.error('Logout error:', err)
      // Игнорируем ошибки logout, все равно очищаем локальные данные
    } finally {
      // Очищаем локальные данные
      token.value = null
      user.value = null
      localStorage.removeItem('auth_token')
      loading.value = false
      console.log('Logout completed')
    }
  }

  const getCurrentUser = async (): Promise<User> => {
    if (!token.value) {
      throw new Error('No token available')
    }

    loading.value = true
    error.value = null

    try {
      const currentUser = await api.auth.getCurrentUser()
      user.value = currentUser
      console.log('Current user loaded:', currentUser)
      return currentUser
    } catch (err: any) {
      error.value = err.detail || 'Ошибка получения данных пользователя'
      console.error('Get current user error:', err)

      // Если токен недействителен, очищаем данные
      if (err.status_code === 401) {
        await logout()
      }

      throw err
    } finally {
      loading.value = false
    }
  }

  const refreshToken = async (): Promise<LoginResponse> => {
    if (!token.value) {
      throw new Error('No token to refresh')
    }

    loading.value = true
    error.value = null

    try {
      const response = await api.auth.refreshToken()

      // Обновляем токен и пользователя
      token.value = response.access_token
      user.value = response.user

      // Сохраняем новый токен
      localStorage.setItem('auth_token', response.access_token)

      return response
    } catch (err: any) {
      error.value = err.detail || 'Ошибка обновления токена'
      console.error('Refresh token error:', err)

      // Если не удалось обновить токен, выходим
      await logout()
      throw err
    } finally {
      loading.value = false
    }
  }

  const updateProfile = async (userData: Partial<User>): Promise<User> => {
    loading.value = true
    error.value = null

    try {
      const updatedUser = await api.users.updateProfile(userData)
      user.value = updatedUser
      return updatedUser
    } catch (err: any) {
      error.value = err.detail || 'Ошибка обновления профиля'
      console.error('Update profile error:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  const changePassword = async (oldPassword: string, newPassword: string): Promise<void> => {
    loading.value = true
    error.value = null

    try {
      await api.users.changePassword(oldPassword, newPassword)
    } catch (err: any) {
      error.value = err.detail || 'Ошибка смены пароля'
      console.error('Change password error:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  const uploadAvatar = async (file: File): Promise<void> => {
    loading.value = true
    error.value = null

    try {
      const fileInfo = await api.users.uploadAvatar(file)

      // Обновляем аватар пользователя
      if (user.value) {
        user.value.avatar = fileInfo.url
      }
    } catch (err: any) {
      error.value = err.detail || 'Ошибка загрузки аватара'
      console.error('Upload avatar error:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  // ИСПРАВЛЕНО: убираем дублирующий вызов
  const initializeAuth = async (): Promise<void> => {
    if (initialized.value) {
      console.log('Auth already initialized')
      return
    }

    console.log('Initializing auth...')
    const savedToken = localStorage.getItem('auth_token')

    if (savedToken) {
      token.value = savedToken
      console.log('Found saved token, attempting to get current user')

      try {
        await getCurrentUser()
        console.log('Auth initialization successful')
      } catch (err) {
        console.error('Failed to initialize auth:', err)
        // Токен недействителен, очищаем
        await logout()
      }
    } else {
      console.log('No saved token found')
    }

    initialized.value = true
  }

  const clearError = (): void => {
    error.value = null
  }

  // Автоматическая инициализация при создании store
  if (!initialized.value) {
    initializeAuth()
  }

  return {
    // State
    user,
    token,
    loading,
    error,
    initialized,

    // Getters
    isAuthenticated,
    isAdmin,

    // Actions
    login,
    register,
    logout,
    getCurrentUser,
    refreshToken,
    updateProfile,
    changePassword,
    uploadAvatar,
    initializeAuth,
    clearError
  }
})
